import { HOSPITAL_URL } from "./url"
import { ApiResponse } from "@/shared/types/interface/api"
import { AxiosError, type AxiosRequestConfig } from "axios"
import clientApi from "@/shared/api/client"
import { GetHospitalFavoriteResponse, GetHospitalReviewResponse } from "@/shared/types/type/hospital"
import { GetHospitalInfoResponseType } from "@/entities/hospital/api/types"
import { ReviewAnalysisResponse, TreatmentCaseSummaryResponse } from "@/entities/hospital/types/type"
import { ReviewDetailResponseDTO, SubjectsAnalysisResponseDTO } from "@/entities/hospital/model/types"

export const getHospitalInfoClient = async (hospitalOriginId: number, locale: string) => {
  try {
    const response = await clientApi.get(HOSPITAL_URL.INFO(hospitalOriginId), {
      headers: {
        "X-Language": locale.toUpperCase(),
      },
    })
    if (response.status !== 200) {
      throw new Error("Failed to get hospital detail")
    }
    if (response.data.status !== 200) {
      const error = new AxiosError(response.data.message || "Failed to get hospital detail")
      error.response = {
        ...response,
        status: response.data.status,
        statusText: response.data.message || "Error",
      } as AxiosError["response"]
      throw error
    }
    return response.data as ApiResponse<GetHospitalInfoResponseType>
  } catch (error) {
    if (error instanceof AxiosError) {
      if (!error.response) {
        throw new Error("No Response")
      }
    }
    throw error
  }
}

// 즐겨찾기 상태는 unrestricted 상세 응답이 아니라 인증 필수 엔드포인트에서 읽는다.
// access token이 없거나 만료되면 401이 발생해야 clientApi interceptor가 refresh를 수행할 수 있다.
export const getHospitalFavoritesClient = async (skipAuthRedirect = false) => {
  const config: AxiosRequestConfig & { skipAuthRedirect: boolean } = { skipAuthRedirect }
  const response = await clientApi.get(HOSPITAL_URL.FAVORITE, config)
  if (response.status !== 200 || response.data.status !== 200) {
    const error = new AxiosError(response.data.message || "Failed to get hospital favorites")
    error.response = {
      ...response,
      status: response.data.status || response.status,
      statusText: response.data.message || "Error",
    } as AxiosError["response"]
    throw error
  }
  return response.data as ApiResponse<GetHospitalFavoriteResponse[]>
}

export const getHospitalReviewClient = async (hospitalOriginId: number, page: number, sort: string) => {
  try {
    const response = await clientApi.get(HOSPITAL_URL.REVIEW_LIST(hospitalOriginId, page, sort))
    if (response.status !== 200) {
      throw new Error("Failed to get hospital review")
    }
    if (response.data.status !== 200) {
      const error = new AxiosError(response.data.message)
      error.response = {
        ...response,
        status: response.data.status,
        statusText: response.data.message || "Error",
      } as AxiosError["response"]
      throw error
    }
    return response.data as ApiResponse<GetHospitalReviewResponse>
  } catch (error) {
    if (error instanceof AxiosError) {
      if (!error.response) {
        throw new Error("No Response")
      }
    }
    throw error
  }
}

export const getCheckHospitalCollectionClient = async (hospitalOriginId: number, locale: string) => {
  try {
    const config: AxiosRequestConfig & { skipAuthRedirect: boolean } = {
      skipAuthRedirect: true,
      headers: {
        "X-Language": locale.toUpperCase(),
      },
    }
    const response = await clientApi.get(HOSPITAL_URL.CHECK_COLLECTION(hospitalOriginId), config)
    if (response.status !== 200) {
      throw new Error("Failed to check hospital collection")
    }
    return response.data as ApiResponse<boolean>
  } catch (error) {
    if (error instanceof AxiosError) {
      if (!error.response) {
        throw new Error("No Response")
      }
    }
    throw error
  }
}

export const getHospitalTreatmentCaseSummaryClient = async (hospitalOriginId: number) => {
  try {
    const response = await clientApi.get(HOSPITAL_URL.TREATMENT_CASE_SUMMARY(hospitalOriginId))
    if (response.status !== 200) {
      throw new Error("Failed to get hospital treatment case summary")
    }
    if (response.data.status !== 200) {
      throw new Error(response.data.message)
    }
    return response.data.result as TreatmentCaseSummaryResponse[]

  } catch (error) {
    if (error instanceof AxiosError) {
      if (!error.response) {
        throw new Error("No Response")
      }
    }
    throw error
  }
}

export const getHospitalReviewAnalysisClient = async (hospitalOriginId: number) => {
  try {
    const response = await clientApi.get(HOSPITAL_URL.REVIEW_ANALYSIS(hospitalOriginId))
    if (response.status !== 200) {
      throw new Error("Failed to get hospital review analysis")
    }
    if (response.data.status !== 200) {
      throw new Error(response.data.message)
    }
    return response.data.result as ReviewAnalysisResponse[]
  } catch (error) {
    if (error instanceof AxiosError) {
      if (!error.response) {
        throw new Error("No Response")
      }
    }
    throw error
  }
}

export const getSubjectsAnalysisClient = async (
  hospitalOriginId: number
): Promise<SubjectsAnalysisResponseDTO[]> => {
  try {
    const response = await clientApi.get(HOSPITAL_URL.SUBJECTS_ANALYSIS(hospitalOriginId))
    if (response.status !== 200) {
      throw new Error("Failed to get subjects analysis")
    }
    if (response.data.status !== 200) {
      throw new Error(response.data.message)
    }
    return response.data.result as SubjectsAnalysisResponseDTO[]
  } catch (error) {
    if (error instanceof AxiosError) {
      if (!error.response) {
        throw new Error("Failed to get subjects analysis")
      }
    }
    throw error
  }
}

export const getReviewDetailClient = async (
  hospitalOriginId: number,
  page: number,
  size: number,
  sort: string,
  subject: string,
  major: string,
  minor: string,
  group: string
): Promise<ReviewDetailResponseDTO> => {
  try {
    const response = await clientApi.get(HOSPITAL_URL.REVIEW_DETAIL(hospitalOriginId, page, size, sort, subject, major, minor, group))
    if (response.status !== 200) {
      throw new Error("Failed to get review detail")
    }
    if (response.data.status !== 200) {
      throw new Error(response.data.message)
    }
    return response.data.result as ReviewDetailResponseDTO
  } catch (error) {
    if (error instanceof AxiosError) {
      if (!error.response) {
        throw new Error("No Response")
      }
    }
    throw error
  }
}

export const postHospitalFavoriteClient = async (isFavorite: boolean, hospitalOriginId: number) => {
  try {
    let response
    if (isFavorite) {
      response = await clientApi.post(HOSPITAL_URL.FAVORITE, {
        hospitalOriginId,
      })
    } else {
      response = await clientApi.delete(HOSPITAL_URL.FAVORITE, {
        data: {
          hospitalOriginId,
        },
      })
    }
    if (response.status !== 200) {
      throw new Error("Failed to post hospital favorite")
    }
    if (response.data.status !== 200) {
      throw new Error("Failed to post hospital favorite")
    }
    return response.data.result
  } catch (error) {
    if (error instanceof AxiosError) {
      if (!error.response) {
        throw new Error("No Response")
      }
    }
    throw error
  }
}
